import React from "react";
import styled, { css } from "styled-components";

// Define row styles
const rowStyles = {
  default: css`
    display: grid;
    align-items: center;
    grid-template-columns: minmax(auto, 14rem) 1fr;
    gap: 2.4rem;
  `,
  withRadioButtons: css`
    display: grid;
    align-items: center;
    grid-template-columns: minmax(auto, 14rem) auto;
    gap: 2.4rem;
  `,
};

const StyledSelect = styled.select`
  width: 100%; // Make it fill the container
  padding: 0.8rem 1.2rem; // Adjust based on your input styling
  border: 1px solid var(--color-grey-300); // Example border, adjust as needed
  border-radius: 4px; // Adjust to match your inputs

  background-color: var(--color-grey-0);
  color: var(--color-grey-900);

  &:disabled {
    background-color: var(--color-grey-100);
    color: var(--color-grey-500);
  }

  &:focus,
  &:active {
    border-color: var(--color-primary);
    outline: none;
    color: var(--color-grey-900); // Ensure text color is visible
    background-color: var(
      --color-grey-0
    ); // Adjust if necessary to ensure visibility
  }
`;

// Define styled components outside of the FormRow function
const StyledFormRow = styled.div`
  padding: 1.2rem 0;
  border-bottom: 1px solid var(--color-grey-100);

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:has(button) {
    display: flex;
    justify-content: flex-end;
    gap: 1.2rem;
  }

  ${(props) => rowStyles[props.$style || "default"]}
`;

const Label = styled.label`
  font-weight: 500;
`;

const Error = styled.span`
  font-size: 1.4rem;
  color: var(--color-red-700);
`;

const RadioButtonLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem; // Adjust this value as needed for space between radio button and text
`;

const RadioButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

// Function component
function FormRow({ label, errors, children, $style = "default" }) {
  const childId = Array.isArray(children)
    ? children[0]?.props?.id
    : children.props.id;

  // Determine if we need to wrap children in a RadioButtonsContainer based on $style
  const isWithRadioButtons = $style === "withRadioButtons";

  return (
    <StyledFormRow $style={$style}>
      {label && <Label htmlFor={childId}>{label}</Label>}
      {/* Conditionally render children based on $style */}
      {isWithRadioButtons ? (
        <RadioButtonsContainer>
          {React.Children.map(children, (child) => {
            // Wrap radio buttons and checkboxes in RadioButtonLabel
            if (
              child &&
              (child.props.type === "radio" || child.props.type === "checkbox")
            ) {
              return (
                <RadioButtonLabel htmlFor={child.props.id}>
                  {React.cloneElement(child)}
                  <span>{child.props.value || "Checkbox"}</span>
                </RadioButtonLabel>
              );
            }
            return child;
          })}
        </RadioButtonsContainer>
      ) : (
        React.Children.map(children, (child) => {
          // Wrap select elements with StyledSelect for consistent styling
          if (child && child.type === "select") {
            return (
              <StyledSelect {...child.props}>
                {child.props.children}
              </StyledSelect>
            );
          }
          // Handle non-radio button children directly
          return child;
        })
      )}
      {errors && <Error>{errors}</Error>}
    </StyledFormRow>
  );
}

export default FormRow;
